import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageHead } from './PageHead.js';
import {
  api,
  formatBRL,
  type CatalogFeed,
  type Collection,
  type JanelaVendas,
  type OfferKind,
  type Product,
} from '../api.js';
import { SHOPEE_CATEGORIES } from '../categories.js';
import {
  CopyAffiliateButton,
  OfferTable,
  sortValue,
  velocity,
  ViewButton,
  type SortKey,
} from './OfferTable.js';

const KINDS: Array<{
  id: OfferKind;
  label: string;
  icon: string;
  source: 'products' | 'shops' | 'campaigns';
}> = [
  { id: 'PRODUCT', label: 'Produtos', icon: '📦', source: 'products' },
  { id: 'SHOP', label: 'Lojas', icon: '🏬', source: 'shops' },
  { id: 'CAMPAIGN', label: 'Campanhas', icon: '🎯', source: 'campaigns' },
];

/** sortType da API — decide o que a Shopee devolve na busca. */
const SEARCH_SORT = [
  { value: 2, label: 'Mais vendidos' },
  { value: 5, label: 'Maior comissão' },
  { value: 4, label: 'Menor preço' },
  { value: 3, label: 'Maior preço' },
  { value: 1, label: 'Relevância (com palavra-chave)' },
];

const SHOP_SORT = [
  { value: 2, label: 'Maior comissão' },
  { value: 3, label: 'Lojas mais populares' },
  { value: 1, label: 'Atualizadas recentemente' },
];

const CAMPAIGN_TYPE_LABEL: Record<number, string> = { 1: 'coleção', 2: 'categoria' };
const SHOP_TYPE_LABEL: Record<number, string> = { 1: 'Mall', 2: 'Preferida', 4: 'Preferida+' };

const DIAS_DA_JANELA: Record<JanelaVendas, number> = { dia: 1, semana: 7, mes: 30 };
const ROTULO_JANELA: Record<JanelaVendas, string> = {
  dia: 'do dia',
  semana: 'da semana',
  mes: 'do mês',
};

/** Estado limpo dos filtros da busca — usado no início e pelo botão "Limpar". */
const PADRAO_FILTROS = {
  keyword: '',
  categoryId: '' as number | '',
  subCategoryId: '' as number | '',
  searchSort: 2,
  minCommission: '',
  keySellerOnly: false,
  xtraOnly: false,
  shopTypes: [] as number[],
};

/**
 * Ordenação inicial de cada tipo: a coluna mais decisiva daquele tipo.
 *
 * Produto se escolhe por giro; loja e campanha, por comissão — não há venda
 * para ordenar.
 */
const ORDEM_PADRAO: Record<OfferKind, SortKey> = {
  PRODUCT: 'sales',
  SHOP: 'commissionRate',
  CAMPAIGN: 'commissionRate',
};

export function Products({ onGoToProduction }: { onGoToProduction: () => void }) {
  const [kind, setKind] = useState<OfferKind>('PRODUCT');
  const [view, setView] = useState<'table' | 'grid'>('table');
  const [items, setItems] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [message, setMessage] = useState<{ kind: 'info' | 'err' | 'warn'; text: string } | null>(
    null,
  );

  // Filtros da BUSCA (vão para a Shopee). Os valores iniciais vivem em
  // PADRAO_FILTROS para o botão "Limpar" e o estado inicial não divergirem.
  const [keyword, setKeyword] = useState(PADRAO_FILTROS.keyword);
  const [categoryId, setCategoryId] = useState<number | ''>(PADRAO_FILTROS.categoryId);
  const [subCategoryId, setSubCategoryId] = useState<number | ''>(
    PADRAO_FILTROS.subCategoryId,
  );
  const [searchSort, setSearchSort] = useState(PADRAO_FILTROS.searchSort);
  const [minCommission, setMinCommission] = useState(PADRAO_FILTROS.minCommission);
  const [keySellerOnly, setKeySellerOnly] = useState(PADRAO_FILTROS.keySellerOnly);
  const [xtraOnly, setXtraOnly] = useState(PADRAO_FILTROS.xtraOnly);
  const [shopTypes, setShopTypes] = useState<number[]>([...PADRAO_FILTROS.shopTypes]);
  /**
   * Loja escolhida na aba de Ofertas.
   *
   * Guarda id E nome: o id busca na Shopee e filtra a mesa, o nome aparece na
   * tela sem depender de a loja estar carregada na lista naquele momento.
   */
  const [loja, setLoja] = useState<{ id: string; name: string } | null>(null);
  const [lojasConhecidas, setLojasConhecidas] = useState<Product[]>([]);

  // Coleções da Shopee (as promoções do mês). `colecaoSel` filtra a mesa.
  const [colecoes, setColecoes] = useState<Collection[]>([]);
  const [colecaoSel, setColecaoSel] = useState<string>('');
  const [urlColecao, setUrlColecao] = useState('');
  const [nomeColecao, setNomeColecao] = useState('');
  const [colecaoBusy, setColecaoBusy] = useState<string | null>(null);
  /**
   * Janela de vendas: null = contador absoluto da Shopee (o de sempre).
   *
   * A Shopee não expõe "mais vendidos do dia" na API de afiliado — este ranking
   * é medido por nós, e a cobertura diz quanto histórico já existe.
   */
  const [janela, setJanela] = useState<JanelaVendas | null>(null);
  /** URL colada para busca direta — o caminho quando o nome não acha. */
  const [urlProduto, setUrlProduto] = useState('');
  const [buscandoUrl, setBuscandoUrl] = useState(false);
  const [cobertura, setCobertura] = useState<{ dias: number; produtos: number } | null>(null);

  // Ordenação da TABELA (local, instantânea)
  const [sortKey, setSortKey] = useState<SortKey>(ORDEM_PADRAO.PRODUCT);
  const [sortDesc, setSortDesc] = useState(true);

  const [feeds, setFeeds] = useState<CatalogFeed[]>([]);
  const [showFeeds, setShowFeeds] = useState(false);
  const [feedBusy, setFeedBusy] = useState<string | null>(null);

  const activeKind = KINDS.find((k) => k.id === kind)!;
  const subCategories = useMemo(
    () => SHOPEE_CATEGORIES.find((c) => c.id === categoryId)?.children ?? [],
    [categoryId],
  );

  const load = useCallback(
    async (target: OfferKind) => {
      setLoading(true);
      try {
        const data = await api.products({
          status: 'SUGGESTED',
          kind: target,
          take: 100,
          ...(keyword ? { search: keyword } : {}),
          // Loja só faz sentido em produto: linha de loja não tem "sua loja".
          ...(loja && target === 'PRODUCT' ? { shopId: loja.id } : {}),
          // Coleção filtra a mesa só nos itens daquela lista.
          ...(colecaoSel && target === 'PRODUCT' ? { collectionId: colecaoSel } : {}),
          // Categoria filtra o que já está no banco pelo caminho de categorias.
          ...(subCategoryId
            ? { categoryId: subCategoryId }
            : categoryId
              ? { categoryId }
              : {}),
          ...(minCommission ? { minCommission: Number(minCommission) / 100 } : {}),
          // Janela só existe para produto: loja e campanha não têm contador.
          ...(janela && target === 'PRODUCT' ? { window: janela } : {}),
        });
        setItems(data.items);
        setTotal(data.total);
        setCobertura(data.cobertura ?? null);
      } catch (cause) {
        setMessage({
          kind: 'err',
          text: cause instanceof Error ? cause.message : 'Falha ao carregar as ofertas',
        });
      } finally {
        setLoading(false);
      }
    },
    [keyword, categoryId, subCategoryId, minCommission, loja, janela, colecaoSel],
  );

  useEffect(() => {
    void load(kind);
  }, [load, kind]);

  const sorted = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      const diff = sortValue(a, sortKey) - sortValue(b, sortKey);
      return sortDesc ? -diff : diff;
    });
    return copy;
  }, [items, sortKey, sortDesc]);

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDesc((d) => !d);
    } else {
      setSortKey(key);
      setSortDesc(true); // números: começar do maior é o que se quer ver
    }
  }

  /** Busca por URL: cola o link (mesmo o curto) e o produto entra na mesa. */
  async function buscarPorUrl() {
    const url = urlProduto.trim();
    if (!url) return;
    setBuscandoUrl(true);
    setMessage(null);
    try {
      const r = await api.discoverUrl(url);
      setUrlProduto('');
      setKind('PRODUCT');
      setMessage({
        kind: 'info',
        text:
          r.created > 0
            ? 'Produto adicionado à mesa pela URL.'
            : 'Produto encontrado — ele já estava na mesa.',
      });
      await load('PRODUCT');
    } catch (cause) {
      setMessage({
        kind: 'err',
        text: cause instanceof Error ? cause.message : 'Não consegui buscar por essa URL',
      });
    } finally {
      setBuscandoUrl(false);
    }
  }

  async function discover() {
    setDiscovering(true);
    setMessage(null);
    try {
      const result = await api.discover([activeKind.source], {
        keyword: keyword || null,
        // A Shopee aceita um único productCatId; o nível mais específico manda.
        categoryId: subCategoryId || categoryId || null,
        sortType: searchSort,
        minCommission: minCommission ? Number(minCommission) / 100 : null,
        ...(kind === 'PRODUCT'
          ? {
              isKeySeller: keySellerOnly || null,
              isAMSOffer: xtraOnly || null,
              // Com loja escolhida, a busca traz o catálogo DELA em vez da
              // vitrine geral — é o caminho de quem monta campanha por loja.
              shopId: loja?.id ?? null,
            }
          : {}),
        ...(kind === 'SHOP'
          ? { shopType: shopTypes.length ? shopTypes : null, isKeySeller: keySellerOnly || null }
          : {}),
      });
      setMessage({
        kind: 'info',
        text:
          `Busca concluída: ${result.found} ${activeKind.label.toLowerCase()} avaliadas, ` +
          `${result.created} novas.` +
          (result.sweptCategories
            ? ` Varremos ${result.sweptCategories} categorias: sem palavra-chave, ` +
              'categoria ou loja, a Shopee ignora a ordenação e devolveria uma ' +
              'lista de recomendados sem ranking.'
            : ''),
      });
      await load(kind);
    } catch (cause) {
      setMessage({
        kind: 'err',
        text: cause instanceof Error ? cause.message : 'A busca falhou',
      });
    } finally {
      setDiscovering(false);
    }
  }

  /**
   * "Ver itens" numa linha de loja: escolhe aquela loja na aba de Ofertas.
   *
   * Não é uma busca avulsa — deixa o filtro de loja LIGADO na aba de Ofertas,
   * então a mesa passa a mostrar só o catálogo dela e o botão "Buscar produtos"
   * continua trazendo mais itens da mesma loja. É assim que se garimpa uma loja
   * inteira para montar campanha, em vez de olhar uma leva e perder o contexto.
   */
  async function verItensDaLoja(alvo: Product) {
    if (!alvo.shopeeShopId) {
      setMessage({
        kind: 'err',
        text: 'Esta loja veio de uma busca antiga, sem o id da loja. Busque lojas de novo e tente outra vez.',
      });
      return;
    }
    const escolhida = { id: alvo.shopeeShopId, name: alvo.name };
    setLoja(escolhida);
    setKind('PRODUCT');
    setSortKey(ORDEM_PADRAO.PRODUCT);
    setSortDesc(true);
    setShowFeeds(false);

    setDiscovering(true);
    setMessage(null);
    try {
      const result = await api.discover(['products'], {
        shopId: escolhida.id,
        sortType: searchSort,
        // O piso de comissão da conta continua valendo: a comissão por ITEM é
        // bem menor que a da vitrine da loja, e cortar demais aqui esvazia a
        // mesa sem explicação.
        minCommission: minCommission ? Number(minCommission) / 100 : null,
      });
      setMessage({
        kind: result.found === 0 ? 'warn' : 'info',
        text:
          result.found === 0
            ? `${escolhida.name}: a Shopee não devolveu itens afiliáveis desta loja com os filtros atuais.`
            : `${escolhida.name}: ${result.found} itens avaliados, ${result.created} novos. A mesa está filtrada nesta loja.`,
      });
    } catch (cause) {
      setMessage({
        kind: 'err',
        text: cause instanceof Error ? cause.message : 'Falha ao buscar os itens da loja',
      });
    } finally {
      setDiscovering(false);
    }
    // O load vem do efeito de `loja`/`kind`; chamar aqui duplicaria a consulta.
  }

  /** Lojas já descobertas — é entre elas que o seletor da aba de Ofertas escolhe. */
  const carregarLojas = useCallback(async () => {
    try {
      const data = await api.products({ kind: 'SHOP', take: 100, sort: 'commission' });
      setLojasConhecidas(data.items);
    } catch {
      // Seletor vazio é degradação aceitável: o botão "Ver itens" na aba Lojas
      // continua sendo o caminho principal.
    }
  }, []);

  useEffect(() => {
    void carregarLojas();
  }, [carregarLojas]);

  /** Coleções salvas — alimentam o painel e o filtro por coleção. */
  const carregarColecoes = useCallback(async () => {
    try {
      setColecoes(await api.collections());
    } catch {
      // Painel vazio é degradação aceitável.
    }
  }, []);

  useEffect(() => {
    void carregarColecoes();
  }, [carregarColecoes]);

  /** Adiciona uma coleção pela URL e já puxa os itens dela. */
  async function adicionarColecao() {
    const url = urlColecao.trim();
    if (!url) return;
    setColecaoBusy('add');
    try {
      const r = await api.addCollection(url, nomeColecao.trim() || null);
      setUrlColecao('');
      setNomeColecao('');
      await carregarColecoes();
      setColecaoSel(r.collectionId); // já filtra a mesa nessa coleção
      setMessage({
        kind: 'info',
        text: `Coleção puxada: ${r.found} itens afiliáveis na mesa (de ${r.totalCount.toLocaleString('pt-BR')} na lista).`,
      });
    } catch (cause) {
      setMessage({
        kind: 'err',
        text: cause instanceof Error ? cause.message : 'Não consegui puxar a coleção',
      });
    } finally {
      setColecaoBusy(null);
    }
  }

  /** Re-puxa os itens de uma coleção já salva. */
  async function atualizarColecao(id: string) {
    setColecaoBusy(id);
    try {
      const r = await api.syncCollection(id);
      await carregarColecoes();
      setColecaoSel(id);
      setMessage({ kind: 'info', text: `Coleção atualizada: ${r.found} itens na mesa.` });
    } catch (cause) {
      setMessage({
        kind: 'err',
        text: cause instanceof Error ? cause.message : 'Falha ao atualizar a coleção',
      });
    } finally {
      setColecaoBusy(null);
    }
  }

  /** Remove a coleção (e some do filtro). */
  async function removerColecao(id: string) {
    if (!window.confirm('Remover esta coleção? Os itens continuam na mesa, só perdem a marca da lista.'))
      return;
    setColecaoBusy(id);
    try {
      await api.removeCollection(id);
      if (colecaoSel === id) setColecaoSel('');
      await carregarColecoes();
    } catch (cause) {
      setMessage({
        kind: 'err',
        text: cause instanceof Error ? cause.message : 'Falha ao remover a coleção',
      });
    } finally {
      setColecaoBusy(null);
    }
  }

  async function loadFeeds() {
    setShowFeeds(true);
    setFeedBusy('list');
    try {
      await api.refreshFeeds('FULL');
      setFeeds(await api.feeds());
    } catch (cause) {
      setMessage({
        kind: 'err',
        text: cause instanceof Error ? cause.message : 'Falha ao listar catálogos',
      });
    } finally {
      setFeedBusy(null);
    }
  }

  async function importFeed(feed: CatalogFeed) {
    setFeedBusy(feed.datafeedId);
    setMessage(null);
    try {
      const result = await api.importFeed(feed.datafeedId);
      setMessage({
        kind: result.unmappedColumns.length > 0 ? 'warn' : 'info',
        text:
          `${result.rows} linhas lidas, ${result.found} ofertas gravadas (${result.created} novas).` +
          (result.truncated ? ' O catálogo foi truncado — há mais linhas.' : '') +
          (result.unmappedColumns.length > 0
            ? ` Colunas não reconhecidas: ${result.unmappedColumns.join(', ')}.`
            : ''),
      });
      setFeeds(await api.feeds());
      if (kind === 'PRODUCT') await load('PRODUCT');
    } catch (cause) {
      setMessage({
        kind: 'err',
        text: cause instanceof Error ? cause.message : 'Falha ao importar o catálogo',
      });
    } finally {
      setFeedBusy(null);
    }
  }

  // Atualização otimista: a linha sai na hora, e volta se o servidor recusar.
  async function select(offer: Product) {
    setItems((prev) => prev.filter((p) => p.id !== offer.id));
    setTotal((t) => t - 1);
    try {
      await api.setProductStatus(offer.id, 'SELECTED');
    } catch (cause) {
      setItems((prev) => [offer, ...prev]);
      setTotal((t) => t + 1);
      setMessage({
        kind: 'err',
        text: cause instanceof Error ? cause.message : 'Não foi possível selecionar',
      });
    }
  }

  async function discard(offer: Product) {
    setItems((prev) => prev.filter((p) => p.id !== offer.id));
    setTotal((t) => t - 1);
    try {
      await api.setProductStatus(offer.id, 'DISCARDED');
    } catch {
      setItems((prev) => [offer, ...prev]);
      setTotal((t) => t + 1);
    }
  }

  const sortOptions = kind === 'PRODUCT' ? SEARCH_SORT : SHOP_SORT;

  /**
   * Algum filtro está fora do padrão?
   *
   * Serve para desabilitar o "Limpar" quando não há nada a limpar — botão
   * ativo que não faz nada é pior que botão ausente.
   */
  const temFiltro =
    keyword !== PADRAO_FILTROS.keyword ||
    categoryId !== PADRAO_FILTROS.categoryId ||
    subCategoryId !== PADRAO_FILTROS.subCategoryId ||
    searchSort !== PADRAO_FILTROS.searchSort ||
    minCommission !== PADRAO_FILTROS.minCommission ||
    keySellerOnly !== PADRAO_FILTROS.keySellerOnly ||
    xtraOnly !== PADRAO_FILTROS.xtraOnly ||
    shopTypes.length > 0 ||
    loja !== null ||
    janela !== null ||
    colecaoSel !== '';

  function limparFiltros() {
    setKeyword(PADRAO_FILTROS.keyword);
    setCategoryId(PADRAO_FILTROS.categoryId);
    setSubCategoryId(PADRAO_FILTROS.subCategoryId);
    setSearchSort(PADRAO_FILTROS.searchSort);
    setMinCommission(PADRAO_FILTROS.minCommission);
    setKeySellerOnly(PADRAO_FILTROS.keySellerOnly);
    setXtraOnly(PADRAO_FILTROS.xtraOnly);
    setShopTypes([...PADRAO_FILTROS.shopTypes]);
    setLoja(null);
    setJanela(null);
    setColecaoSel('');
    // Só limpa os campos. Recarregar aqui gastaria uma chamada da Shopee que a
    // pessoa talvez não queira — ela decide clicando em "Aplicar" ou "Buscar".
  }

  const melhorComissao = items.length
    ? Math.max(...items.map((i) => i.commissionRate))
    : null;
  // A hora da varredura vem do produto mais recente: é o dado que já temos, e
  // responde a pergunta real ("isto está fresco?") sem endpoint novo.
  const ultimaVarredura = items.length
    ? new Date(
        Math.max(...items.map((i) => new Date(i.discoveredAt).getTime())),
      ).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <>
      <PageHead
        eyebrow={`Ofertas · ${activeKind.label}`}
        title="O que vale divulgar hoje"
        description="Produtos, lojas inteiras e campanhas da Shopee. Compare as métricas, selecione o que vai divulgar — tudo segue pelo mesmo fluxo de produção."
        readout={[
          { label: 'na mesa', value: total },
          // O corte de 12% é o que separa "vale o post" de "não paga o
          // trabalho" — por isso ele merece um número próprio, não um filtro
          // que a pessoa precisa aplicar para descobrir.
          items.length > 0 && {
            label: 'acima de 12%',
            value: items.filter((i) => i.commissionRate >= 0.12).length,
            tom: 'gain' as const,
          },
          melhorComissao && {
            label: 'melhor comissão',
            value: `${(melhorComissao * 100).toFixed(1)}%`,
            tom: 'gain' as const,
          },
          ultimaVarredura && {
            label: 'última varredura',
            value: ultimaVarredura,
            tom: 'quiet' as const,
          },
        ]}
        actions={
          <>
          <div className="view-toggle">
            <button
              type="button"
              aria-current={view === 'table' ? 'true' : undefined}
              onClick={() => setView('table')}
              title="Tabela comparativa"
            >
              ☰ Tabela
            </button>
            <button
              type="button"
              aria-current={view === 'grid' ? 'true' : undefined}
              onClick={() => setView('grid')}
              title="Grade com imagens"
            >
              ▦ Grade
            </button>
          </div>
            <button type="button" className="primary" onClick={discover} disabled={discovering}>
              {discovering ? (
                <span className="spinner" />
              ) : (
                `Buscar ${activeKind.label.toLowerCase()}`
              )}
            </button>
          </>
        }
      />

      {/* Abas por tipo de oferta */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 14, flexWrap: 'wrap' }}>
        {KINDS.map((k) => (
          <button
            key={k.id}
            type="button"
            className="tab"
            aria-current={kind === k.id && !showFeeds ? 'page' : undefined}
            onClick={() => {
              setKind(k.id);
              setShowFeeds(false);
              // Cada tipo mostra colunas diferentes. Manter "Vendas" ao trocar
              // para Campanhas deixaria a tabela numa ordem que nenhuma coluna
              // visível explica.
              setSortKey(ORDEM_PADRAO[k.id]);
              setSortDesc(true);
            }}
          >
            {k.icon} {k.label}
          </button>
        ))}
        <button
          type="button"
          className="tab"
          aria-current={showFeeds ? 'page' : undefined}
          onClick={() => (showFeeds ? setShowFeeds(false) : void loadFeeds())}
          title="Catálogos em massa: mais econômico no limite de 8.000 chamadas/hora"
        >
          🗂 Catálogos
        </button>
      </div>

      {/* Busca por URL: o atalho para quando o nome não acha. Fica ACIMA dos
          filtros porque não interage com nenhum deles — é outro caminho. */}
      {kind === 'PRODUCT' && (
        <div className="card" style={{ padding: '10px 14px', marginBottom: 10 }}>
          <div className="row" style={{ gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <label htmlFor="f-url">Buscar por URL do produto</label>
              <input
                id="f-url"
                value={urlProduto}
                onChange={(e) => setUrlProduto(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void buscarPorUrl();
                }}
                placeholder="cole o link da Shopee (página do produto ou link curto s.shopee…)"
              />
            </div>
            <button
              type="button"
              className="primary"
              disabled={buscandoUrl || !urlProduto.trim()}
              onClick={() => void buscarPorUrl()}
            >
              {buscandoUrl ? <span className="spinner" /> : 'Adicionar à mesa'}
            </button>
          </div>
          <p className="muted" style={{ fontSize: 12, margin: '6px 0 0' }}>
            Aceita o link da página do produto (mesmo com aqueles parâmetros gigantes) e o
            link curto. Ignora o piso de comissão: você já decidiu divulgar este.
          </p>
        </div>
      )}

      {/* Coleções da Shopee — as promoções do mês. Adicione a URL da lista e
          puxe os itens; clique numa coleção para filtrar a mesa só nela. */}
      {kind === 'PRODUCT' && (
        <div className="card" style={{ padding: '10px 14px', marginBottom: 10 }}>
          <div className="row" style={{ gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <label htmlFor="f-col">Coleções da Shopee (promoções do mês)</label>
              <input
                id="f-col"
                value={urlColecao}
                onChange={(e) => setUrlColecao(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void adicionarColecao();
                }}
                placeholder="cole o link da lista: https://shopee.com.br/collections/NUMERO"
              />
            </div>
            <div style={{ width: 200 }}>
              <label htmlFor="f-col-nome">Nome (opcional)</label>
              <input
                id="f-col-nome"
                value={nomeColecao}
                onChange={(e) => setNomeColecao(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void adicionarColecao();
                }}
                placeholder="ex: Choice Poste e Ganhe"
              />
            </div>
            <button
              type="button"
              className="primary"
              disabled={colecaoBusy !== null || !urlColecao.trim()}
              onClick={() => void adicionarColecao()}
            >
              {colecaoBusy === 'add' ? <span className="spinner" /> : 'Adicionar e puxar'}
            </button>
          </div>

          {colecoes.length > 0 && (
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
              {/* "Todas" limpa o filtro de coleção. */}
              <button
                type="button"
                className={colecaoSel ? 'ghost' : 'primary'}
                style={{ fontSize: 12.5, padding: '4px 10px' }}
                onClick={() => setColecaoSel('')}
              >
                Todas
              </button>
              {colecoes.map((c) => {
                const ativo = colecaoSel === c.collectionId;
                const nome = c.title || `Coleção ${c.collectionId}`;
                return (
                  <span
                    key={c.id}
                    className="chip"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '4px 8px',
                      borderRadius: 8,
                      border: '1px solid var(--border)',
                      background: ativo ? 'var(--accent-dim, var(--surface-2))' : 'var(--surface-2)',
                      fontSize: 12.5,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setColecaoSel(ativo ? '' : c.collectionId)}
                      title="Filtrar a mesa só nesta coleção"
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        color: ativo ? 'var(--accent)' : 'var(--text)',
                        fontWeight: ativo ? 700 : 500,
                      }}
                    >
                      {nome}
                      <span className="muted" style={{ marginLeft: 6, fontWeight: 400 }}>
                        {c.syncedCount} na mesa
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => void atualizarColecao(c.collectionId)}
                      disabled={colecaoBusy !== null}
                      title="Puxar os itens de novo"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      {colecaoBusy === c.collectionId ? <span className="spinner" /> : '↻'}
                    </button>
                    <button
                      type="button"
                      onClick={() => void removerColecao(c.collectionId)}
                      disabled={colecaoBusy !== null}
                      title="Remover esta coleção"
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        color: 'var(--text-dim)',
                      }}
                    >
                      ✕
                    </button>
                  </span>
                );
              })}
            </div>
          )}
          <p className="muted" style={{ fontSize: 12, margin: '8px 0 0' }}>
            As listas mudam todo mês — adicione as novas e remova as antigas. Puxamos só os
            itens afiliáveis (com comissão e link). Coleções grandes trazem os primeiros itens;
            use ↻ para atualizar.
          </p>
        </div>
      )}

      {/* Filtros da busca */}
      <div className="card filters">
        <div className="field" style={{ minWidth: 180 }}>
          <label htmlFor="f-kw">Palavra-chave</label>
          <input
            id="f-kw"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => {
              // Enter busca na Shopee, não filtra o que já está na mesa: o
              // filtro local já roda a cada tecla (keyword está no load).
              // Quem digita o nome de uma loja que ainda não buscou esperava
              // uma busca — antes via a tabela vazia e concluía, errado, que a
              // loja não era afiliada.
              if (e.key === 'Enter') void discover();
            }}
            placeholder={kind === 'SHOP' ? 'ex: choice oficial' : 'ex: air fryer'}
          />
        </div>

        {kind === 'PRODUCT' && (
          <div className="field" style={{ minWidth: 178 }}>
            <label htmlFor="f-janela">Mais vendidos</label>
            <select
              id="f-janela"
              value={janela ?? ''}
              onChange={(e) => setJanela((e.target.value || null) as JanelaVendas | null)}
              title="Ranking pela venda MEDIDA no período — a Shopee não expõe isso na API"
            >
              <option value="">Total acumulado</option>
              <option value="dia">Do dia</option>
              <option value="semana">Da semana</option>
              <option value="mes">Do mês</option>
            </select>
          </div>
        )}


        {kind === 'PRODUCT' && (
          <div className="field" style={{ minWidth: 210 }}>
            <label htmlFor="f-loja">Loja</label>
            <select
              id="f-loja"
              value={loja?.id ?? ''}
              onChange={(e) => {
                const id = e.target.value;
                if (!id) {
                  setLoja(null);
                  return;
                }
                const achada = lojasConhecidas.find((l) => l.shopeeShopId === id);
                setLoja({ id, name: achada?.name ?? `Loja ${id}` });
              }}
              title="Mostra só os itens desta loja e restringe a busca a ela"
            >
              <option value="">Todas as lojas</option>
              {/* A loja escolhida entra na lista mesmo se não estiver entre as
                  carregadas — vindo do botão "Ver itens", ela pode não estar. */}
              {loja && !lojasConhecidas.some((l) => l.shopeeShopId === loja.id) && (
                <option value={loja.id}>{loja.name}</option>
              )}
              {lojasConhecidas
                .filter((l) => l.shopeeShopId)
                .map((l) => (
                  <option key={l.id} value={l.shopeeShopId!}>
                    {l.name}
                  </option>
                ))}
            </select>
          </div>
        )}

        {kind === 'PRODUCT' && (
          <>
            <div className="field" style={{ minWidth: 190 }}>
              <label htmlFor="f-cat">Categoria</label>
              <select
                id="f-cat"
                value={categoryId}
                onChange={(e) => {
                  setCategoryId(e.target.value ? Number(e.target.value) : '');
                  setSubCategoryId(''); // trocar de categoria invalida a subcategoria
                }}
              >
                <option value="">Todas as categorias</option>
                {SHOPEE_CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {subCategories.length > 0 && (
              <div className="field" style={{ minWidth: 190 }}>
                <label htmlFor="f-sub">Subcategoria</label>
                <select
                  id="f-sub"
                  value={subCategoryId}
                  onChange={(e) =>
                    setSubCategoryId(e.target.value ? Number(e.target.value) : '')
                  }
                >
                  <option value="">Toda a categoria</option>
                  {subCategories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}

        <div className="field" style={{ minWidth: 165 }}>
          <label htmlFor="f-sort">Buscar ordenado por</label>
          <select
            id="f-sort"
            value={searchSort}
            onChange={(e) => setSearchSort(Number(e.target.value))}
          >
            {sortOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="field" style={{ width: 110 }}>
          <label htmlFor="f-comm">Comissão mín.</label>
          <input
            id="f-comm"
            type="number"
            min={0}
            max={100}
            step={1}
            value={minCommission}
            onChange={(e) => setMinCommission(e.target.value)}
            placeholder="%"
          />
        </div>

        {kind === 'PRODUCT' && (
          <>
            <label className="check">
              <input
                type="checkbox"
                checked={xtraOnly}
                onChange={(e) => setXtraOnly(e.target.checked)}
              />
              Só com Comissão Xtra
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={keySellerOnly}
                onChange={(e) => setKeySellerOnly(e.target.checked)}
              />
              Só key sellers
            </label>
          </>
        )}

        {kind === 'SHOP' && (
          <div className="checks">
            {[1, 2, 4].map((t) => (
              <label key={t} className="check">
                <input
                  type="checkbox"
                  checked={shopTypes.includes(t)}
                  onChange={(e) =>
                    setShopTypes((prev) =>
                      e.target.checked ? [...prev, t] : prev.filter((x) => x !== t),
                    )
                  }
                />
                {SHOP_TYPE_LABEL[t]}
              </label>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, flex: '0 0 auto' }}>
          <button type="button" onClick={() => void load(kind)} disabled={loading}>
            Aplicar aos já buscados
          </button>
          <button
            type="button"
            className="ghost"
            onClick={limparFiltros}
            disabled={!temFiltro}
            title={
              temFiltro
                ? 'Voltar todos os filtros ao padrão'
                : 'Os filtros já estão no padrão'
            }
          >
            Limpar filtros
          </button>
        </div>
      </div>

      {message && <div className={`notice ${message.kind}`}>{message.text}</div>}

      {/* Catálogos em massa */}
      {showFeeds ? (
        <section className="card" style={{ padding: 18 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
            <p className="section-title" style={{ margin: 0 }}>
              Catálogos em massa
            </p>
            <button
              type="button"
              className="ghost"
              onClick={() => void loadFeeds()}
              disabled={feedBusy !== null}
            >
              {feedBusy === 'list' ? <span className="spinner" /> : '↻ atualizar'}
            </button>
          </div>
          <p className="muted" style={{ marginTop: 0 }}>
            Importa uma categoria inteira de uma vez (500 itens por chamada, contra 50 da busca
            normal). Vale quando o volume crescer — economiza muito do limite de 8.000
            chamadas/hora.
          </p>

          {feeds.length === 0 ? (
            <div className="empty">Nenhum catálogo disponível.</div>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {feeds.map((feed) => (
                <div
                  key={feed.id}
                  style={{
                    display: 'flex',
                    gap: 12,
                    alignItems: 'center',
                    padding: '9px 0',
                    borderTop: '1px solid var(--border)',
                  }}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ fontSize: 13 }}>{feed.name}</strong>
                    <span className="badge" style={{ marginLeft: 8 }}>
                      {feed.feedMode}
                    </span>
                    <span className="muted" style={{ display: 'block' }}>
                      {feed.totalCount} itens
                      {feed.feedDate ? ` · ${feed.feedDate}` : ''}
                      {feed.importedAt
                        ? ` · importado: ${feed.importedRows} linhas, ${feed.importedNew} novas`
                        : ' · nunca importado'}
                    </span>
                    {feed.importError && (
                      <span className="muted" style={{ color: 'var(--err)' }}>
                        erro: {feed.importError}
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => void importFeed(feed)}
                    disabled={feedBusy !== null}
                  >
                    {feedBusy === feed.datafeedId ? <span className="spinner" /> : 'Importar'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      ) : loading ? (
        <div className="empty">
          <span className="spinner" /> Carregando…
        </div>
      ) : sorted.length === 0 ? (
        <div className="card empty">
          <strong>Nenhuma sugestão de {activeKind.label.toLowerCase()}</strong>
          Ajuste os filtros e clique em “Buscar {activeKind.label.toLowerCase()}”.
          <div style={{ marginTop: 14 }}>
            <button type="button" className="ghost" onClick={onGoToProduction}>
              Ir para Produção →
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Honestidade da medição: "mais vendidos do mês" com 3 dias de
              histórico é uma meia-verdade. O número de dias fica à vista. */}
          {janela && kind === 'PRODUCT' && cobertura && (
            <div
              className={cobertura.dias < DIAS_DA_JANELA[janela] ? 'notice warn' : 'notice'}
              style={{ marginBottom: 10, fontSize: 12.5 }}
            >
              {cobertura.dias < DIAS_DA_JANELA[janela] ? (
                <>
                  Medindo há <strong>{cobertura.dias} dia(s)</strong> — a janela “
                  {ROTULO_JANELA[janela]}” pede {DIAS_DA_JANELA[janela]}. O ranking já
                  vale, mas cobre menos tempo do que o nome diz. A Shopee não expõe
                  vendas por período na API de afiliado; este número é medido aqui.
                </>
              ) : (
                <>
                  Vendas medidas por nós ao longo de {cobertura.dias} dias, em{' '}
                  {cobertura.produtos} produtos acompanhados. Ranqueia o que buscamos,
                  não a Shopee inteira.
                </>
              )}
            </div>
          )}

          {/* Filtro de loja visível: sem isto, um filtro que não pegou parece
              lista normal — foi exatamente como o parâmetro perdido passou. */}
          {loja && kind === 'PRODUCT' && (
            <div
              className="row"
              style={{
                gap: 8,
                alignItems: 'center',
                marginBottom: 10,
                padding: '7px 11px',
                border: '1px solid var(--line)',
                borderRadius: 'var(--r-control)',
                background: 'var(--surface-2)',
                fontSize: 12.5,
              }}
            >
              <span aria-hidden="true">🏬</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                Mostrando só <strong>{loja.name}</strong> · {total} itens
              </span>
              <button type="button" className="ghost" onClick={() => setLoja(null)}>
                ✕ todas as lojas
              </button>
            </div>
          )}

          <p className="muted" style={{ marginTop: 0, marginBottom: 10 }}>
            {total} {activeKind.label.toLowerCase()} aguardando decisão
            {view === 'table' && ' · clique no cabeçalho para ordenar'}
          </p>

          {view === 'table' ? (
            <OfferTable
              items={sorted}
              sortKey={sortKey}
              sortDesc={sortDesc}
              onSort={toggleSort}
              onSelect={(o) => void select(o)}
              onDiscard={(o) => void discard(o)}
              onShopItems={(o) => void verItensDaLoja(o)}
            />
          ) : (
            <div className="grid">
              {sorted.map((offer) => (
                <OfferCard
                  key={offer.id}
                  offer={offer}
                  onSelect={() => void select(offer)}
                  onDiscard={() => void discard(offer)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
}

function OfferCard({
  offer,
  onSelect,
  onDiscard,
}: {
  offer: Product;
  onSelect: () => void;
  onDiscard: () => void;
}) {
  const shopTypes = Array.isArray(offer.shopType) ? offer.shopType : [];
  const endsAt = offer.offerEndsAt ? new Date(offer.offerEndsAt) : null;
  const endingSoon =
    endsAt !== null && endsAt.getTime() - Date.now() < 3 * 86_400_000 && endsAt > new Date();
  const delta = velocity(offer);

  return (
    <article className="product">
      {offer.imageUrl ? (
        <img src={offer.imageUrl} alt="" loading="lazy" />
      ) : (
        <div style={{ aspectRatio: 1, background: 'var(--surface-2)' }} />
      )}
      <div className="product-body">
        <div className="product-name" title={offer.name}>
          {offer.name}
        </div>

        {offer.kind === 'PRODUCT' ? (
          <div className="product-price">
            {formatBRL(offer.priceCents)}
            {offer.priceDiscountRate ? (
              <span className="badge ok" style={{ marginLeft: 7 }}>
                -{offer.priceDiscountRate}%
              </span>
            ) : null}
          </div>
        ) : (
          <div className="product-price" style={{ fontSize: 14 }}>
            {(offer.commissionRate * 100).toFixed(1)}% de comissão
          </div>
        )}

        <div className="product-meta">
          {offer.kind === 'PRODUCT' && (
            <>
              <span title="Comissão por venda">
                💰 {formatBRL(offer.commissionCents)} ({(offer.commissionRate * 100).toFixed(1)}%)
              </span>
              {offer.sellerCommissionRate ? (
                <span title="Comissão Xtra: parte paga pelo vendedor">
                  ✨ +{(offer.sellerCommissionRate * 100).toFixed(1)}%
                </span>
              ) : null}
              <span title="Vendas acumuladas">📦 {offer.sales.toLocaleString('pt-BR')}</span>
              {delta !== null && delta > 0 && (
                <span style={{ color: 'var(--ok)' }} title="Vendas desde a busca anterior">
                  ↑ {delta.toLocaleString('pt-BR')}
                </span>
              )}
            </>
          )}
          {offer.ratingStar !== null && <span>⭐ {offer.ratingStar.toFixed(1)}</span>}
          {offer.kind === 'SHOP' &&
            shopTypes.map((t) => (
              <span key={t} className="badge accent">
                {SHOP_TYPE_LABEL[t] ?? `tipo ${t}`}
              </span>
            ))}
          {offer.kind === 'SHOP' && offer.remainingBudget !== null && (
            <span title="Orçamento restante da oferta">
              🎫 {offer.remainingBudget.toLocaleString('pt-BR')}
            </span>
          )}
          {offer.kind === 'CAMPAIGN' && offer.campaignOfferType !== null && (
            <span className="badge">
              {CAMPAIGN_TYPE_LABEL[offer.campaignOfferType] ?? 'campanha'}
            </span>
          )}
          {endingSoon && (
            <span className="badge warn" title={endsAt!.toLocaleString('pt-BR')}>
              acaba em breve
            </span>
          )}
        </div>

        <div className="product-actions">
          <ViewButton offer={offer} />
          <CopyAffiliateButton offer={offer} />
          <button type="button" className="primary" onClick={onSelect}>
            Selecionar
          </button>
          <button type="button" className="ghost" onClick={onDiscard} title="Descartar">
            ✕
          </button>
        </div>
      </div>
    </article>
  );
}
